import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';

export function ControlsPage() {
  const { sectionColor } = useNavigation();

  return (
    <div className="space-y-6">
      <ComingSoonWatermark
        pageKey="controls-register-main"
        icon={CheckCircle}
        title="Control Register Coming Soon"
        description="Comprehensive control management with testing schedules, effectiveness tracking, and evidence management will be available soon."
      />
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-3xl font-bold", sectionColor)}>Control Register</h1>
          <p className="text-neutral-400 mt-1">
            Track control effectiveness and testing schedules
          </p>
        </div>
        <button className="btn-primary">
          Add Control
        </button>
      </div>

      {/* Metrics Strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Total Controls</p>
              <p className="text-2xl font-bold text-white mt-1">342</p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <CheckCircle className="text-primary-400" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Effective</p>
              <p className="text-2xl font-bold text-success-500 mt-1">298</p>
            </div>
            <div className="p-3 bg-success-500/20 rounded-lg">
              <CheckCircle className="text-success-500" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Testing Due</p>
              <p className="text-2xl font-bold text-warning-500 mt-1">28</p>
            </div>
            <div className="p-3 bg-warning-500/20 rounded-lg">
              <Clock className="text-warning-500" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Failed Tests</p>
              <p className="text-2xl font-bold text-danger-500 mt-1">16</p>
            </div>
            <div className="p-3 bg-danger-500/20 rounded-lg">
              <XCircle className="text-danger-500" size={24} />
            </div>
          </div>
        </Card>
      </div>

      {/* Controls Table */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Control Effectiveness</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Control ID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Description</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Framework</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Effectiveness</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Last Tested</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm font-mono text-primary-400">CTRL-001</td>
                <td className="py-3 px-4 text-sm text-white">Multi-factor authentication</td>
                <td className="py-3 px-4 text-sm text-neutral-300">ISO 27001</td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-24 bg-neutral-800 rounded-full overflow-hidden">
                      <div className="h-full bg-success-500" style={{width: '95%'}}></div>
                    </div>
                    <span className="text-sm text-neutral-300">95%</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-15</td>
                <td className="py-3 px-4"><span className="badge-success">Effective</span></td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm font-mono text-primary-400">CTRL-002</td>
                <td className="py-3 px-4 text-sm text-white">Data encryption at rest</td>
                <td className="py-3 px-4 text-sm text-neutral-300">NIST CSF</td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-24 bg-neutral-800 rounded-full overflow-hidden">
                      <div className="h-full bg-success-500" style={{width: '92%'}}></div>
                    </div>
                    <span className="text-sm text-neutral-300">92%</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-28</td>
                <td className="py-3 px-4"><span className="badge-success">Effective</span></td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm font-mono text-primary-400">CTRL-003</td>
                <td className="py-3 px-4 text-sm text-white">Access control review process</td>
                <td className="py-3 px-4 text-sm text-neutral-300">DORA</td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-24 bg-neutral-800 rounded-full overflow-hidden">
                      <div className="h-full bg-warning-500" style={{width: '72%'}}></div>
                    </div>
                    <span className="text-sm text-neutral-300">72%</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-09-20</td>
                <td className="py-3 px-4"><span className="badge-warning">Testing Due</span></td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm font-mono text-primary-400">CTRL-004</td>
                <td className="py-3 px-4 text-sm text-white">Incident response procedures</td>
                <td className="py-3 px-4 text-sm text-neutral-300">SOC 2</td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-24 bg-neutral-800 rounded-full overflow-hidden">
                      <div className="h-full bg-danger-500" style={{width: '58%'}}></div>
                    </div>
                    <span className="text-sm text-neutral-300">58%</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-05</td>
                <td className="py-3 px-4"><span className="badge-danger">Failed Test</span></td>
              </tr>
              <tr className="hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm font-mono text-primary-400">CTRL-005</td>
                <td className="py-3 px-4 text-sm text-white">Backup and recovery testing</td>
                <td className="py-3 px-4 text-sm text-neutral-300">ISO 27001</td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-24 bg-neutral-800 rounded-full overflow-hidden">
                      <div className="h-full bg-success-500" style={{width: '88%'}}></div>
                    </div>
                    <span className="text-sm text-neutral-300">88%</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-22</td>
                <td className="py-3 px-4"><span className="badge-success">Effective</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
